import { useTranslation } from 'react-i18next'

import i18n from '@/locales/i18n'
import { tenantApi } from '@/services/api/config/api'
import { useUpdateUserSettingsMutation } from '@/services/api/tenant/userEnhanced'
import { RootState, useTypedDispatch, useTypedSelector } from '@/store'
import { useToast } from './toast'

// Persist the user's UI language, switch i18next, then drop the RTK Query cache: course and
// training titles are localised by the backend from the persisted language, so every cached
// payload is stale after a switch (BUG-113). Desktop and mobile both change language through
// this hook so the two paths cannot drift apart again.
export const useChangeUiLanguage = () => {
  const dispatch = useTypedDispatch()
  const user = useTypedSelector((state: RootState) => state.authSlice.user)
  const [updateUserSettings] = useUpdateUserSettingsMutation()
  const { t } = useTranslation('general')
  const toast = useToast()

  // Resolves to true when the language was switched, false when nothing changed.
  const changeUiLanguage = async (languageTag: string): Promise<boolean> => {
    if (!languageTag || languageTag === i18n.language) return false

    try {
      if (user?.user_id && user?.tenant_id) {
        await updateUserSettings({
          tenantId: user.tenant_id,
          userId: user.user_id,
          updateUserSettingsDto: { language_tag: languageTag },
        }).unwrap()
        await i18n.changeLanguage(languageTag)
        dispatch(tenantApi.util.resetApiState())
      } else {
        // Nobody to persist for and no user-scoped cache to drop; switch locally only.
        await i18n.changeLanguage(languageTag)
      }
      return true
    } catch (error) {
      console.error('Failed to update language:', error)
      toast.error(t('error_contact_support'))
      return false
    }
  }

  return { changeUiLanguage }
}
